/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bindAll } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl-next';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import {
	AIActionShortcutMenu,
	AIActionShortcutMenuIconOnly,
} from '../../../ai-action-shortcut-menu';
import { AIButton } from '../../../ai-button';
import { AIProactiveNudge } from '../../../ai-proactive-nudge';
import type { AISmartButtonProps } from '../../../common/types/shared-types';
import { isBrowser } from '../../../common/utils/is-browser';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label',
		defaultMessage: 'Ask Rovo AI',
		description: 'Label for the AI smart button tooltip',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
});

type AISmartButtonUIProps = Pick<
	AISmartButtonProps,
	'product' | 'productPage' | 'sidebarApi' | 'analyticsAttributes' | 'isDisabled' | 'getActions'
> & {
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
};

export const AISmartButtonUI = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			sidebarApi,
			product,
			productPage,
			analyticsAttributes,
			getActions,
		},
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();

		const [canShowNudge, setCanShowNudge] = useState(false);
		const [spotlightRendered, setSpotlightRendered] = useState(false);
		const [isHovering, setIsHovering] = useState(false);
		const [actionShortcuts, setActionShortcuts] = useState<ConversationAction[]>([]);
		const [hasActiveNudge, setHasActiveNudge] = useState(false);
		const placementElementRef = useRef<HTMLDivElement | null>(null);
		const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

		useEffect(() => {
			if (getActions) {
				getActions()
					.then((fetchedActions) => {
						// Filter out nested actions since they are not supported in the action shortcuts
						const filteredActions = fetchedActions.filter(
							(action) => action.handleType !== HandleActionType.Nested,
						);
						const shortcuts = filteredActions.slice(0, 3) || [];
						setActionShortcuts(shortcuts);
					})
					.catch(() => {
						setActionShortcuts([]);
					});
			}
		}, [getActions]);

		const onboardingSpotlightElementId = useMemo(() => {
			const isLiveOrEditPage =
				analyticsAttributes?.pageMode === 'live' || analyticsAttributes?.pageMode === 'edit';
			const isPageOrBlogpost = productPage === 'page' || productPage === 'blogpost';

			if (isPageOrBlogpost && isLiveOrEditPage) {
				return `rovo-button-onboarding-spotlight-live-or-edit`;
			}

			return `rovo-button-onboarding-spotlight`;
		}, [productPage, analyticsAttributes]);

		// Handle "Get Started" button click from spotlight
		const handleGetStartedClick = useCallback(() => {
			if (sidebarApi) {
				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});
			}
		}, [sidebarApi, toggleConversationAssistantUIMode]);

		// Set up event listeners for spotlight communication
		useEffect(() => {
			if (!isBrowser() || !placementElementRef.current || !fg('rovo_button_onboarding_spotlight')) {
				return;
			}

			const doc = getDocument();

			if (!doc) {
				return;
			}

			// Listen for spotlight rendered event
			const handleSpotlightRendered = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					setSpotlightRendered(true);
				}
			};

			// Listen for "Get Started" button click event
			const handleGetStartedEvent = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					handleGetStartedClick();
				}
			};

			const unbind = bindAll(doc, [
				{
					type: 'rovo-button-onboarding-spotlight-rendered',
					listener: handleSpotlightRendered,
				},
				{
					type: 'rovo-button-onboarding-spotlight-get-started-click',
					listener: handleGetStartedEvent,
				},
			]);

			return () => {
				unbind();
			};
		}, [handleGetStartedClick, onboardingSpotlightElementId]);

		useEffect(() => {
			if (spotlightRendered) {
				return;
			}

			if (!fg('rovo_button_onboarding_spotlight') && !canShowNudge) {
				setCanShowNudge(true);
				return;
			}

			// Give spotlight time to render (if it will) since it needs to run decisions logic
			const timeout = setTimeout(() => {
				if (!spotlightRendered && !canShowNudge) {
					setCanShowNudge(true);
				}
			}, 1000);

			return () => clearTimeout(timeout);
		}, [spotlightRendered, canShowNudge]);

		const nudgeOrPlacement = useMemo(() => {
			return (
				<React.Fragment>
					{fg('rovo_button_onboarding_spotlight') && (
						<div id={onboardingSpotlightElementId} ref={placementElementRef} />
					)}
					{canShowNudge && (
						<AIProactiveNudge
							sidebarApi={sidebarApi}
							product={product}
							onActiveNudgeChange={setHasActiveNudge}
						/>
					)}
				</React.Fragment>
			);
		}, [sidebarApi, product, canShowNudge, onboardingSpotlightElementId]);

		const handleButtonMouseEnter = useCallback(() => {
			setIsHovering(true);
			onMouseEnter();
		}, [onMouseEnter]);

		const handleButtonMouseLeave = useCallback(() => {
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
				onMouseLeave();
			}, 500);
		}, [onMouseLeave]);

		const handleActionBoxMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			setIsHovering(true);
		}, []);

		const handleActionBoxMouseLeave = useCallback(() => {
			setIsHovering(false);
		}, []);

		const canShowActionShortcuts = useMemo(() => {
			return actionShortcuts.length > 0 && !hasActiveNudge && !spotlightRendered;
		}, [actionShortcuts.length, hasActiveNudge, spotlightRendered]);

		const actionShortcutsRendering = useMemo(() => {
			if (!isHovering || !canShowActionShortcuts) {
				return null;
			}

			switch (
				FeatureGates.getExperimentValue<string>(
					'confluence_rovo_button_shortcuts_experiment',
					'cohort',
					'control',
				)
			) {
				case 'action-shortcut-icon-only':
					return (
						<AIActionShortcutMenuIconOnly
							actionShortcuts={actionShortcuts}
							onMouseEnter={handleActionBoxMouseEnter}
							onMouseLeave={handleActionBoxMouseLeave}
							product={product}
							sidebarApi={sidebarApi}
							analyticsAttributes={{
								...analyticsAttributes,
								experiment: 'confluence_rovo_button_shortcuts_experiment',
								cohort: 'action-shortcut-icon-only',
							}}
						/>
					);
				case 'action-shortcut-icon-and-text':
					return (
						<AIActionShortcutMenu
							actionShortcuts={actionShortcuts}
							onMouseEnter={handleActionBoxMouseEnter}
							onMouseLeave={handleActionBoxMouseLeave}
							product={product}
							sidebarApi={sidebarApi}
							analyticsAttributes={{
								...analyticsAttributes,
								experiment: 'confluence_rovo_button_shortcuts_experiment',
								cohort: 'action-shortcut-icon-and-text',
							}}
						/>
					);
				default:
					return null;
			}
		}, [
			isHovering,
			canShowActionShortcuts,
			actionShortcuts,
			handleActionBoxMouseEnter,
			handleActionBoxMouseLeave,
			sidebarApi,
			product,
			analyticsAttributes,
		]);

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{nudgeOrPlacement}
				<Tooltip
					position="left"
					content={formatMessage(i18n.tooltipLabel)}
					hideTooltipOnClick
					ignoreTooltipPointerEvents
					canAppear={() => {
						// If control, always show tooltip
						// If not control, show tooltip only when shortcuts cannot be shown
						return (
							!canShowActionShortcuts ||
							FeatureGates.getExperimentValue<string>(
								'confluence_rovo_button_shortcuts_experiment',
								'cohort',
								'control',
							) === 'control'
						);
					}}
				>
					<AIButton
						onClick={onClick}
						isDisabled={isDisabled}
						onMouseEnter={handleButtonMouseEnter}
						onMouseLeave={handleButtonMouseLeave}
						ref={ref}
					/>
				</Tooltip>
				{actionShortcutsRendering}
			</Inline>
		);
	},
);
